import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { USER } from "../../constants/constant";
import "./leaderboard.css";

const PhleboLeaderboard = () => {
	const history = useHistory();
	const [leaderboardData, setLeaderboardData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [viewType, setViewType] = useState("pan-india");
	const currentPhlebo = USER.user;

	useEffect(() => {
		fetchLeaderboardData();
	}, []);

	const handleBack = () => {
		history.goBack();
	};

	const fetchLeaderboardData = async () => {
		setLoading(true);
		setError(null);

		try {
			const response = await fetch(
				`https://mb-dronahq-prod.medibuddy.in/automation/webhook/69c275fe93cc94917cb58405`,
				{
					method: "GET",
					headers: {
						Cookie:
							"DSSID=s%3AWudO3NCYQWIVkzQqbH9wcVTg7u1l2ZLW.NJ6fwPK%2BiUZNpsG4ro998uHgYMQgXrH61BCKj9efipw",
					},
				},
			);

			if (!response.ok) {
				throw new Error("Failed to fetch leaderboard data");
			}

			const result = await response.json();
			setLeaderboardData(result.data || []);
			setLoading(false);
		} catch (error) {
			console.error("Error fetching leaderboard:", error);
			setError(error.message);
			setLoading(false);
		}
	};

	const toggleView = (type) => {
		setViewType(type);
	};

	const getFilteredData = useMemo(() => {
		let filtered = leaderboardData;

		if (viewType === "city" && currentPhlebo && currentPhlebo.city) {
			filtered = leaderboardData.filter(
				(row) => row.city === currentPhlebo.city,
			);
		}

		const top10 = filtered.slice(0, 50);

		// If current phlebo is not in top 10, add them as last row
		if (currentPhlebo && currentPhlebo.phleboId) {
			const currentPhleboInTop10 = top10.some(
				(row) => row.phleboid === currentPhlebo.phleboId,
			);
			if (!currentPhleboInTop10) {
				const currentPhleboData = filtered.find(
					(row) => row.phleboid === currentPhlebo.phleboId,
				);
				if (currentPhleboData) {
					return [...top10, currentPhleboData];
				}
			}
		}

		return top10;
	}, [leaderboardData, viewType, currentPhlebo]);

	const currentPhleboData = useMemo(() => {
		if (!currentPhlebo || !currentPhlebo.phleboId) return null;
		return leaderboardData.find(
			(row) => row.phleboid === currentPhlebo.phleboId,
		);
	}, [leaderboardData, currentPhlebo]);

	const currentPhleboRank = useMemo(() => {
		return leaderboardData.findIndex(
			(row) => currentPhlebo && row.phleboid === currentPhlebo.phleboId,
		);
	}, [leaderboardData, currentPhlebo]);

	const getCurrentMonth = () => {
		const months = [
			"January",
			"February",
			"March",
			"April",
			"May",
			"June",
			"July",
			"August",
			"September",
			"October",
			"November",
			"December",
		];
		const currentDate = new Date();
		return `${months[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
	};

	const getRankIcon = (index) => {
		if (index === 0) {
			return <span className="rank-medal gold-medal">🥇</span>;
		} else if (index === 1) {
			return <span className="rank-medal silver-medal">🥈</span>;
		} else if (index === 2) {
			return <span className="rank-medal bronze-medal">🥉</span>;
		}
		return <span className="rank-number">{index + 1}</span>;
	};

	return (
		<div className="leaderboard-container">
			<header className="leaderboard-header">
				<nav className="navbar navbar-dark bg-primary">
					<button className="back-button" onClick={handleBack}>
						<i className="fa fa-arrow-left"></i>
					</button>
					<div className="navbar-brand">
						<h2 className="leaderboard-title">
							Phlebo Leaderboard - {getCurrentMonth()}
						</h2>
					</div>
				</nav>
			</header>

			{!loading && !error && (
				<div className="toggle-container">
					<button
						className={`toggle-btn ${viewType === "pan-india" ? "active" : ""}`}
						onClick={() => toggleView("pan-india")}
					>
						PAN India
					</button>
					<button
						className={`toggle-btn ${viewType === "city" ? "active" : ""}`}
						onClick={() => toggleView("city")}
					>
						My City{" "}
						{currentPhlebo && currentPhlebo.city
							? `(${currentPhlebo.city})`
							: ""}
					</button>
				</div>
			)}

			{loading && (
				<div className="loading-container">
					<div className="spinner"></div>
					<p>Loading leaderboard...</p>
				</div>
			)}

			{error && (
				<div className="error-container">
					<p className="error-message">Error: {error}</p>
				</div>
			)}

			{!loading && !error && (
				<div className="metric-card-container">
					<div className="metric-card fade-in">
						<div className="card-header">
							{viewType === "pan-india"
								? "PAN India Rankings"
								: `${currentPhlebo?.city || "City"} Rankings`}
						</div>
						<div className="card-body">
							<table className="leaderboard-table">
								<thead>
									<tr>
										<th>Rank</th>
										<th>Name</th>
										<th>City</th>
										<th>Tests</th>
									</tr>
								</thead>
								<tbody>
									{getFilteredData.length > 0 ? (
										getFilteredData.map((row, index) => {
											const isCurrentPhlebo =
												currentPhlebo &&
												row.phleboid === currentPhlebo.phleboId;
											const actualRank =
												viewType === "pan-india"
													? leaderboardData.findIndex(
															(r) => r.phleboid === row.phleboid,
														)
													: index;

											return (
												<tr
													key={row.phleboid}
													className={`table-row slide-in ${isCurrentPhlebo ? "current-phlebo-row" : ""}`}
													style={{ animationDelay: `${index * 0.05}s` }}
												>
													<td className="rank-cell">
														{getRankIcon(actualRank)}
													</td>
													<td className="name-cell">
														{row.name}
														{isCurrentPhlebo && (
															<span className="you-badge">YOU</span>
														)}
													</td>
													<td className="city-cell">{row.city}</td>
													<td className="orders-cell">
														<span className="orders-badge">
															{row.totalorders}
														</span>
													</td>
												</tr>
											);
										})
									) : (
										<tr>
											<td colSpan="4" className="no-data">
												No data available
											</td>
										</tr>
									)}
								</tbody>
							</table>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default PhleboLeaderboard;
